import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type MagazineBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string; italic?: boolean }
  | { type: "blockquote"; text: string }
  | { type: "hr" };

export type MagazineArticleRow = {
  id: string;
  slug: string;
  magazine: string;
  title: string;
  dek: string;
  excerpt: string;
  body: MagazineBlock[];
  display_date: string;
  iso_date: string;
  published: boolean;
};

function parseBody(raw: string): MagazineBlock[] {
  const blocks: MagazineBlock[] = [];
  const chunks = raw.replace(/\r\n/g, "\n").split(/\n\s*\n/);
  for (const chunk of chunks) {
    const line = chunk.trim();
    if (!line) continue;
    if (line === "---") {
      blocks.push({ type: "hr" });
    } else if (line.startsWith("### ")) {
      blocks.push({ type: "h3", text: line.slice(4).trim() });
    } else if (line.startsWith("## ")) {
      blocks.push({ type: "h2", text: line.slice(3).trim() });
    } else if (line.startsWith("> ")) {
      blocks.push({ type: "blockquote", text: line.slice(2).trim() });
    } else {
      blocks.push({ type: "p", text: line });
    }
  }
  return blocks;
}

async function assertAdminCtx(supabase: any, userId: string) {
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (!(roles ?? []).some((r: { role: string }) => r.role === "admin")) {
    throw new Error("Unauthorized");
  }
}

export const listPublishedMagazine = createServerFn({ method: "GET" }).handler(
  async (): Promise<MagazineArticleRow[]> => {
    const { data, error } = await supabaseAdmin
      .from("magazine_articles")
      .select("*")
      .eq("published", true)
      .order("iso_date", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as MagazineArticleRow[];
  },
);

const slugSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9-]+$/, "Invalid slug"),
});

export const getMagazineBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => slugSchema.parse(d))
  .handler(async ({ data }): Promise<MagazineArticleRow | null> => {
    const { data: row, error } = await supabaseAdmin
      .from("magazine_articles")
      .select("*")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row ?? null) as unknown as MagazineArticleRow | null;
  });

export const listAdminMagazine = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MagazineArticleRow[]> => {
    await assertAdminCtx(context.supabase, context.userId);
    const { data, error } = await supabaseAdmin
      .from("magazine_articles")
      .select("*")
      .order("iso_date", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as MagazineArticleRow[];
  });


const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(160)
    .regex(/^[a-z0-9-]+$/, "Slug: küçük harf, rakam ve tire"),
  magazine: z.string().min(1).max(120),
  title: z.string().min(1).max(300),
  dek: z.string().max(2000).default(""),
  excerpt: z.string().max(1000).default(""),
  body_raw: z.string().min(1).max(200000),
  display_date: z.string().max(120).default(""),
  iso_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  published: z.boolean().default(true),
});

export const upsertMagazine = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => upsertSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdminCtx(context.supabase, context.userId);
    const body = parseBody(data.body_raw);
    const payload = {
      slug: data.slug,
      magazine: data.magazine,
      title: data.title,
      dek: data.dek,
      excerpt: data.excerpt,
      body,
      display_date: data.display_date,
      iso_date: data.iso_date,
      published: data.published,
    };
    if (data.id) {
      const { error } = await supabaseAdmin
        .from("magazine_articles")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await supabaseAdmin
      .from("magazine_articles")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteMagazine = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdminCtx(context.supabase, context.userId);
    const { error } = await supabaseAdmin
      .from("magazine_articles")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

